import { Slot } from '@radix-ui/react-slot';
import type { RenderActivation, RenderLayout, RenderNode, RenderStyle, RenderResponsiveRule } from '@inf-monkeys-tech/monkeys';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  CSSProperties,
  HTMLAttributes,
  MutableRefObject,
  ReactNode,
  Ref,
} from 'react';

import { cn } from '../base/utils';

export type RenderNodeHostElement = 'article' | 'aside' | 'div' | 'main' | 'nav' | 'section';

export interface RenderNodeScrollPosition {
  x: number;
  y: number;
}

export interface RenderNodeScrollRestoration {
  read(key: string): RenderNodeScrollPosition | undefined;
  write(key: string, position: RenderNodeScrollPosition): void;
  remove?(key: string): void;
}

export interface RenderNodePolicyState {
  active: boolean;
  visible: boolean;
  mounted: boolean;
  queryEnabled: boolean;
}

export interface RenderNodeRuntimeState extends RenderNodePolicyState {
  elementProps?: HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> };
  activate(): void;
  setManualQueryEnabled(enabled: boolean): void;
}

export interface RenderNodeHostProps
  extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  node: RenderNode;
  as?: RenderNodeHostElement;
  asChild?: boolean;
  unframed?: boolean;
  active?: boolean;
  visible?: boolean;
  manualQueryEnabled?: boolean;
  children?: ReactNode | ((runtime: RenderNodeRuntimeState) => ReactNode);
  loading?: ReactNode;
  empty?: ReactNode;
  error?: ReactNode;
  elementRef?: Ref<HTMLElement>;
  scrollRestoration?: RenderNodeScrollRestoration | false;
  onActivate?: (activation: RenderActivation, node: RenderNode) => void;
  onManualQueryEnabledChange?: (enabled: boolean, node: RenderNode) => void;
  onRuntimeStateChange?: (state: RenderNodePolicyState, node: RenderNode) => void;
}

const refIdentity = (reference: RenderNode['pageRef'] | undefined) => reference?.id;

export const getRenderNodeDataAttributes = (node: RenderNode) => ({
  'data-monkeys-node-id': node.nodeId,
  'data-monkeys-node-kind': node.kind,
  'data-monkeys-node-version': node.version,
  'data-monkeys-owner': node.ownerRepo,
  'data-monkeys-parent-node': node.parentNodeId,
  'data-monkeys-slot': node.slot,
  'data-monkeys-page-id': refIdentity(node.pageRef),
  'data-monkeys-semantic-id': refIdentity(node.semanticRef),
  'data-monkeys-data-context-id': refIdentity(node.dataContextRef),
  'data-monkeys-capability-id': refIdentity(node.capabilityRef),
  'data-monkeys-provider-id': refIdentity(node.providerRef),
  'data-monkeys-state-id': refIdentity(node.stateRef),
  'data-monkeys-frame-owner': node.surface.frameOwner,
  'data-monkeys-surface-tone': node.surface.tone,
  'data-monkeys-density': node.surface.density,
  'data-monkeys-scroll-owner': node.scroll.owner,
  'data-monkeys-scroll-axis': node.scroll.axis,
  'data-monkeys-scroll-restore-key': node.scroll.restoreKey,
  'data-monkeys-virtualization-boundary': node.scroll.virtualizationBoundary,
  'data-monkeys-activation-id': node.activation.activationId,
  'data-monkeys-activation-mode': node.activation.mode,
  'data-monkeys-mount-policy': node.lifecycle.mountPolicy,
  'data-monkeys-query-policy': node.lifecycle.queryPolicy,
  'data-monkeys-deep-link': node.lifecycle.deepLink,
  'data-monkeys-focus-return': node.lifecycle.focusReturn,
  'data-monkeys-layout-mode': node.layout.mode,
  'data-monkeys-responsive-rules': node.responsive.length,
  'data-monkeys-child-count': node.children.length,
  'data-monkeys-access-id': refIdentity(node.accessRef),
  'data-monkeys-evidence-id': refIdentity(node.evidenceRef),
  'data-monkeys-render-state': node.state,
});

const sessionStorageKey = (key: string) => `monkeys.render.scroll.${key}`;

export const sessionRenderNodeScrollRestoration: RenderNodeScrollRestoration = {
  read(key) {
    if (typeof window === 'undefined') return undefined;
    try {
      const value = window.sessionStorage.getItem(sessionStorageKey(key));
      if (!value) return undefined;
      const parsed = JSON.parse(value) as Partial<RenderNodeScrollPosition>;
      return typeof parsed.x === 'number' && typeof parsed.y === 'number'
        ? { x: parsed.x, y: parsed.y }
        : undefined;
    } catch {
      return undefined;
    }
  },
  write(key, position) {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(sessionStorageKey(key), JSON.stringify(position));
    } catch {
      // Storage may be unavailable in private or embedded browser contexts.
    }
  },
  remove(key) {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(sessionStorageKey(key));
    } catch {
      // Storage may be unavailable in private or embedded browser contexts.
    }
  },
};

export const resolveRenderNodeLayout = (node: RenderNode, viewportWidth?: number): RenderLayout => {
  if (viewportWidth === undefined) return node.layout;
  const matching = node.responsive.filter((rule) =>
    (rule.minWidthPx === undefined || viewportWidth >= rule.minWidthPx)
    && (rule.maxWidthPx === undefined || viewportWidth <= rule.maxWidthPx));
  return matching.length === 0 ? node.layout : matching[matching.length - 1].layout;
};

export const resolveRenderNodeResponsiveStyle = (
  style: RenderStyle | undefined,
  responsive: readonly (RenderResponsiveRule & { style?: RenderStyle })[],
  viewportWidth?: number,
): RenderStyle | undefined => {
  const matching = viewportWidth === undefined
    ? []
    : responsive.filter((rule) =>
      (rule.minWidthPx === undefined || viewportWidth >= rule.minWidthPx)
      && (rule.maxWidthPx === undefined || viewportWidth <= rule.maxWidthPx)
      && rule.style,
    );
  if (matching.length === 0) return style;
  return matching.reduce<RenderStyle>(
    (resolved, rule) => ({ ...resolved, ...rule.style }),
    { ...(style ?? {}) },
  );
};

const LAYOUT_MODE_CLASSES: Record<RenderLayout['mode'], string> = {
  contents: 'contents',
  block: 'block',
  flex: 'flex',
  grid: 'grid',
  absolute: 'absolute',
};
const ALIGN_CLASSES = { start: 'items-start', center: 'items-center', end: 'items-end', stretch: 'items-stretch' } as const;
const JUSTIFY_CLASSES = { start: 'justify-start', center: 'justify-center', end: 'justify-end', between: 'justify-between', around: 'justify-around' } as const;

const tokenPathToCssVariable = (value: string): string => {
  const path = value.slice(1).trim();
  if (!/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/i.test(path)) throw new TypeError(`Invalid Design Token path ${value}.`);
  return `var(--monkeys-${path.replace(/\./g, '-').replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()})`;
};

export const resolveRenderNodeStyle = (style: RenderStyle | undefined): CSSProperties | undefined =>
  style
    ? (Object.fromEntries(Object.entries(style).map(([property, value]) => [property, typeof value === 'string' && value.startsWith('$') ? tokenPathToCssVariable(value) : value])) as CSSProperties)
    : undefined;

export const getRenderLayoutClassName = (layout: RenderLayout): string => cn(
  LAYOUT_MODE_CLASSES[layout.mode],
  layout.mode === 'flex' && (layout.direction === 'row' ? 'flex-row' : 'flex-col'),
  layout.align && ALIGN_CLASSES[layout.align],
  layout.justify && JUSTIFY_CLASSES[layout.justify],
);

const shouldMountNode = (node: RenderNode, active: boolean, visible: boolean) => {
  if (node.lifecycle.mountPolicy === 'always') return true;
  if (node.lifecycle.mountPolicy === 'when-visible') return visible;
  return active;
};

const shouldQueryNode = (
  node: RenderNode,
  active: boolean,
  visible: boolean,
  manualQueryEnabled: boolean,
) => {
  if (node.lifecycle.queryPolicy === 'always') return true;
  if (node.lifecycle.queryPolicy === 'when-visible') return visible;
  if (node.lifecycle.queryPolicy === 'when-active') return active;
  return manualQueryEnabled;
};

export const resolveRenderNodePolicyState = (
  node: RenderNode,
  input: {
    active: boolean;
    visible: boolean;
    manualQueryEnabled?: boolean;
    previouslyMounted?: boolean;
  },
): RenderNodePolicyState => {
  const policyMounted = shouldMountNode(node, input.active, input.visible);
  return {
    active: input.active,
    visible: input.visible,
    mounted: policyMounted || (node.lifecycle.retainOnDeactivate && input.previouslyMounted === true),
    queryEnabled: shouldQueryNode(node, input.active, input.visible, input.manualQueryEnabled === true),
  };
};

const resolveRenderNodeContent = (
  node: RenderNode,
  content: ReactNode,
  loading: ReactNode,
  empty: ReactNode,
  error: ReactNode,
) => {
  if (node.state === 'loading' && loading !== undefined) return loading;
  if (node.state === 'empty' && empty !== undefined) return empty;
  if (node.state === 'error' && error !== undefined) return error;
  return content;
};

const assignRef = (ref: Ref<HTMLElement> | undefined, value: HTMLElement | null) => {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as MutableRefObject<HTMLElement | null>).current = value;
};

export const RenderNodeHost = forwardRef<HTMLElement, RenderNodeHostProps>(function RenderNodeHost({
  node,
  as = 'div',
  active = true,
  visible = active,
  manualQueryEnabled = false,
  asChild = false,
  unframed = false,
  children,
  loading,
  empty,
  error,
  elementRef,
  scrollRestoration = sessionRenderNodeScrollRestoration,
  onActivate,
  onManualQueryEnabledChange,
  onRuntimeStateChange,
  onScroll,
  className,
  hidden,
  role,
  'aria-live': ariaLive,
  'aria-atomic': ariaAtomic,
  ...props
}: RenderNodeHostProps, forwardedRef) {
  const Component = asChild ? Slot : as;
  const hostRef = useRef<HTMLElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number | undefined>(() =>
    typeof window === 'undefined' ? undefined : window.innerWidth);
  const [mountedNodeId, setMountedNodeId] = useState<string | undefined>(() =>
    shouldMountNode(node, active, visible) ? node.nodeId : undefined);
  const policyMounted = shouldMountNode(node, active, visible);

  useEffect(() => {
    if (node.responsive.length === 0 || typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [node.responsive.length]);

  const resolvedLayout = useMemo(() => resolveRenderNodeLayout(node, viewportWidth), [node, viewportWidth]);
  const resolvedStyle = useMemo(
    () => resolveRenderNodeResponsiveStyle(node.style, node.responsive, viewportWidth),
    [node.responsive, node.style, viewportWidth],
  );

  useEffect(() => {
    if (!active || !visible || !node.lifecycle.deepLink || !['page', 'record'].includes(node.kind)) return;
    const element = hostRef.current;
    if (!element || element.style.display === 'contents') return;
    element.focus({ preventScroll: true });
  }, [active, node.kind, node.lifecycle.deepLink, node.nodeId, visible]);

  useEffect(() => {
    if (policyMounted) setMountedNodeId(node.nodeId);
    else if (!node.lifecycle.retainOnDeactivate) setMountedNodeId(undefined);
  }, [node.lifecycle.retainOnDeactivate, node.nodeId, policyMounted]);

  const policyState = useMemo(() => resolveRenderNodePolicyState(node, {
    active,
    visible,
    manualQueryEnabled,
    previouslyMounted: mountedNodeId === node.nodeId,
  }), [active, manualQueryEnabled, mountedNodeId, node, visible]);

  const activate = useCallback(() => {
    onActivate?.(node.activation, node);
  }, [node, onActivate]);

  const setManualQueryEnabled = useCallback((enabled: boolean) => {
    onManualQueryEnabledChange?.(enabled, node);
  }, [node, onManualQueryEnabledChange]);

  const runtimeState = useMemo<RenderNodeRuntimeState>(() => ({
    ...policyState,
    activate,
    setManualQueryEnabled,
  }), [activate, policyState, setManualQueryEnabled]);

  useEffect(() => {
    onRuntimeStateChange?.(policyState, node);
  }, [node, onRuntimeStateChange, policyState]);

  const setHostRef = useCallback((value: HTMLElement | null) => {
    hostRef.current = value;
    assignRef(elementRef, value);
    assignRef(forwardedRef, value);
  }, [elementRef, forwardedRef]);

  const scrollAdapter = scrollRestoration === false ? undefined : scrollRestoration;
  const restoresHostScroll = scrollAdapter !== undefined
    && node.scroll.owner !== 'provider'
    && !node.scroll.virtualizationBoundary
    && node.scroll.axis !== 'none'
    && node.scroll.restoreKey !== undefined;

  const readPosition = useCallback((): RenderNodeScrollPosition => {
    const element = hostRef.current;
    if (!element) return { x: 0, y: 0 };
    return {
      x: node.scroll.axis === 'y' ? 0 : element.scrollLeft,
      y: node.scroll.axis === 'x' ? 0 : element.scrollTop,
    };
  }, [node.scroll.axis]);

  useEffect(() => {
    if (!policyState.mounted || !restoresHostScroll || !scrollAdapter) return undefined;
    const key = node.scroll.restoreKey!;
    const element = hostRef.current;
    const position = scrollAdapter.read(key);
    let frame: number | undefined;
    if (element && position) {
      const restore = () => element.scrollTo({
        left: node.scroll.axis === 'y' ? element.scrollLeft : position.x,
        top: node.scroll.axis === 'x' ? element.scrollTop : position.y,
        behavior: 'auto',
      });
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        frame = window.requestAnimationFrame(restore);
      } else {
        restore();
      }
    }
    return () => {
      if (frame !== undefined && typeof window !== 'undefined') window.cancelAnimationFrame(frame);
      scrollAdapter.write(key, readPosition());
    };
  }, [node.scroll.axis, node.scroll.restoreKey, policyState.mounted, readPosition, restoresHostScroll, scrollAdapter]);

  if (!policyState.mounted) return null;

  const elementProps: NonNullable<RenderNodeRuntimeState['elementProps']> & Record<`data-${string}`, unknown> = {
    ...props,
    ...getRenderNodeDataAttributes(node),
    ref: setHostRef,
    className: cn((!unframed || resolvedLayout.mode !== 'contents') && getRenderLayoutClassName(resolvedLayout), className),
    style: {
      ...(resolvedLayout.mode === 'grid' && resolvedLayout.columns ? { gridTemplateColumns: `repeat(${resolvedLayout.columns}, minmax(0, 1fr))` } : {}),
      ...resolveRenderNodeStyle(resolvedStyle),
      ...props.style,
    },
    hidden: hidden || !policyState.visible || undefined,
    role: role ?? (node.state === 'error' ? 'alert' : node.state === 'loading' || node.state === 'empty' ? 'status' : undefined),
    tabIndex: props.tabIndex ?? (node.lifecycle.deepLink && ['page', 'record'].includes(node.kind) ? -1 : undefined),
    'data-monkeys-active': policyState.active,
    'data-monkeys-mounted': policyState.mounted,
    'data-monkeys-query-enabled': policyState.queryEnabled,
    'aria-busy': node.state === 'loading' || undefined,
    'aria-disabled': node.state === 'disabled' || undefined,
    'aria-hidden': !policyState.visible || undefined,
    'aria-live': ariaLive ?? (node.state === 'error' ? 'assertive' : node.state === 'loading' || node.state === 'empty' ? 'polite' : undefined),
    'aria-atomic': ariaAtomic ?? (node.state === 'loading' || node.state === 'empty' || node.state === 'error' ? true : undefined),
    onScroll: (event) => {
      if (restoresHostScroll && scrollAdapter && node.scroll.restoreKey) scrollAdapter.write(node.scroll.restoreKey, readPosition());
      onScroll?.(event);
    },
  };
  const renderedChildren = typeof children === 'function'
    ? children(unframed ? { ...runtimeState, elementProps } : runtimeState)
    : children;
  if (unframed) return <>{resolveRenderNodeContent(node, renderedChildren, loading, empty, error)}</>;

  return (
    <Component {...elementProps} ref={setHostRef as never}
    >
      {resolveRenderNodeContent(node, renderedChildren, loading, empty, error)}
    </Component>
  );
});
